import { useQuery } from '@tanstack/react-query';
import { getAlertasBajas } from '@lib/services/bajas.service';
import { getLegajosListosCounts } from '@lib/services/altas-masivas.service';
import { listarAdherentes } from '@lib/services/adherentes.service';
import { puedeVer, type ViewKey } from '@lib/permissions/catalog';
import type { User } from '@lib/types';

/**
 * Cuántos pendientes tiene cada sección, indexado por RUTA y no por permiso.
 * Un permiso puede gobernar más de una pantalla —`/bajas` y `/alertas-bajas`
 * compartían `view:bajas` hasta que se separaron— y en ese caso indexar por
 * permiso le pone el mismo badge a las dos.
 */
export type Pendientes = Partial<Record<string, number>>;

/** Cada cuánto se refrescan los contadores del menú. */
const REFRESCO_MS = 60_000;

/**
 * Contadores de "pendientes" para los badges del menú.
 *
 * Se piden con `pageSize: 1`: de cada endpoint solo interesa el `total`, no las
 * filas. Y solo se piden las secciones que el usuario PUEDE ver — si no, un
 * USUARIO_EMPRESA dispararía tres consultas que le van a dar 403.
 *
 * Cada contador cuenta lo ACCIONABLE: lo que el operador puede despachar hoy
 * desde esa pantalla. Un badge que incluya lo que está frenado por otra cosa no
 * baja nunca y deja de significar algo.
 *
 *  - **Alertas Bajas**: alertas en estado `abierta`.
 *  - **Alta Grupos**: legajos listos para enviar (`pendientes.ok`). NO se suman
 *    las categorías frenadas —observaciones, preexistencias, bloqueo—: esas
 *    esperan que se resuelva otra cosa, no que alguien las despache desde esta
 *    pantalla, y sumarlas daba un número que no coincidía con lo que se ve.
 *  - **Alta Integrantes**: bis que se pueden enviar YA (`totalEnviables`), que
 *    exige que el titular haya activado. El total de bis incluye los que están
 *    esperando esa activación, que tampoco son accionables desde acá.
 *
 * Si una consulta falla, esa sección queda SIN badge en vez de mostrar 0. Un
 * cero es una afirmación ("no tenés nada pendiente") y sería mentira: preferimos
 * no decir nada a decir algo falso.
 */
export function usePendientes(user: User | null): Pendientes {
  const ve = (k: ViewKey) => Boolean(user && puedeVer(user, k));

  const alertas = useQuery({
    queryKey: ['pendientes', 'alertas-bajas'],
    queryFn: () => getAlertasBajas({ page: 1, pageSize: 1, estado: 'abierta' }),
    enabled: ve('view:alertas-bajas'),
    refetchInterval: REFRESCO_MS,
    retry: false,
  });

  const altaGrupos = useQuery({
    queryKey: ['pendientes', 'alta-grupos'],
    queryFn: getLegajosListosCounts,
    enabled: ve('view:legajos-listos'),
    refetchInterval: REFRESCO_MS,
    retry: false,
  });

  const altaIntegrantes = useQuery({
    queryKey: ['pendientes', 'alta-integrantes'],
    queryFn: () => listarAdherentes({ page: 1, pageSize: 1 }),
    enabled: ve('view:adherentes'),
    refetchInterval: REFRESCO_MS,
    retry: false,
  });

  const p: Pendientes = {};
  if (alertas.data) p['/alertas-bajas'] = alertas.data.total;
  if (altaGrupos.data) p['/legajos-listos'] = altaGrupos.data.pendientes.ok.legajos;
  if (altaIntegrantes.data) p['/legajos-bis'] = altaIntegrantes.data.totalEnviables;
  return p;
}
